import { APP_STAGE } from "@config-global";
import { LangHelper } from "@jnpmedi/helper-session";
import { Location } from "history";
import isEmail from "validator/lib/isEmail";
import { DEFAULT_PAGE, DEFAULT_RECORD_PER_PAGE, PaginationProps } from "@newComponents/common/DocsTable/DocsTablePagination";
import { MvnLanguage, MvnMessage as MvnMessageLib } from "@maven-msg/lib/Message";
import { BundleItemFunctionalityType } from "@maven-rest/billing/Bundle.dto";
import { MySubscription, MySubscriptionLicense } from "@maven-rest/billing/Subscription.dto";
import { isSubscribedFunctionality } from "@maven-rest/billing/SubscriptionLicense.lib";
import { isEnumVal } from "@maven-rest/common/common.types";
import { HttpRequest } from "@maven-rest/common/HttpRequest";
import { DocumentSignStatus } from "@maven-rest/docs/DocumentSign.dto";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderFileCriteria } from "@maven-rest/docs/FolderFile.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";

export const langHelper = new LangHelper({ stage: APP_STAGE });

export type UserAgentOS = "Windows" | "macOS" | "Linux" | "iOS" | "Android" | "Unknown";

export type DocumentSignMergePollingType = {
  fileExportHistoryId: bigint;
  isPause: boolean;
};

export function MvnMessage(...args: Parameters<typeof MvnMessageLib>) {
  const browserLang = langHelper.getLang();
  const [mvn, binds, naked, lang] = args;

  const mvnLang = lang
    ? lang // 1. lang 입력
    : isEnumVal(MvnLanguage, browserLang)
    ? browserLang // 2. 브라우저 기본값(스토리지/쿠키)
    : undefined; // 3. MvnMessage 기본값

  return MvnMessageLib(mvn, binds, naked, mvnLang);
}

export function toDigitString(num: number, digits: number = 2): string {
  return num.toString().padStart(digits, "0");
}
export type TablePageType = Omit<PaginationProps, "count">;

export function calculateTableRowNumber(rowIndex: number, pageInfo: TablePageType): number {
  const { page, rpp } = pageInfo;
  return 1 + rowIndex + rpp * (page - 1);
}

// NOTE: Grebob Migration 과정 중, organization 관련 잘못 저장된 key가 있어 삭제하는 함수
export function cleanupUnusedStorage() {
  const key = "DOCS:undefined:ORG";

  if (localStorage.getItem(key) !== null) {
    if (APP_STAGE === "local") {
      console.info(`[CleanUp] Removing unused key: ${key}`);
    }

    localStorage.removeItem(key);
  }
}

export function setDefaultHttpTimezoneOffsetHeader() {
  HttpRequest.setAdditionalHeaders({ "Timezone-Offset": String(-new Date().getTimezoneOffset()) });
}

export type TextFieldFormErrorType = "INVALID" | "WARNING" | "EMAIL_INCLUDE_KOREAN";

type UpdateQueryParamsOptions = {
  pathname?: string;
  replace?: boolean;
};

export function createUpdatedSearchURL(
  location: Location,
  newParams: Record<string, string | number | undefined>,
  options?: UpdateQueryParamsOptions
): string {
  const searchParams = new URLSearchParams(location.search);

  Object.entries(newParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, String(value));
    }
  });

  const pathname = options?.pathname || location.pathname;
  const query = searchParams.toString();

  return query ? `${pathname}?${query}` : pathname;
}

export const isFileSizeExceedsLimit = (file: File, limitInMB: number): boolean => {
  return file.size >= limitInMB * 1024 * 1024;
};

export function cleanEmptyParams<T extends Record<string, unknown>>(search: T) {
  const newSearch = { ...search };
  Object.keys(newSearch).forEach((key) => {
    const value = newSearch[key];
    if (value === undefined || value === "" || (typeof value === "number" && isNaN(value)) || (Array.isArray(value) && value.length === 0))
      delete newSearch[key];
  });

  if (search.pageIndex === DEFAULT_PAGE) delete newSearch.pageIndex;
  if (search.pageSize === DEFAULT_RECORD_PER_PAGE) delete newSearch.pageSize;

  return newSearch;
}

export function parseSearchParams(search: string): Record<string, any> {
  const params = new URLSearchParams(search);
  const result: Record<string, any> = {};

  for (const [key, value] of params.entries()) {
    if (value === "true") {
      result[key] = true;
    } else if (value === "false") {
      result[key] = false;
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function createSearchString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  }

  return searchParams.toString();
}

export function isValidPhone(phone: string): boolean {
  return /^(01[016789]{1})[0-9]{3,4}[0-9]{4}$/.test(phone);
}

export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent;

  // 기본 모바일 디바이스 감지
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  // 인앱 브라우저 감지 (Instagram, Facebook, KakaoTalk, Line 등)
  const inAppBrowserRegex =
    /KAKAOTALK|Snapchat|Line|WirtschaftsWoche|WeChat|Thunderbird|Instagram|everytimeApp|WhatsApp|Electron|wadiz|AliApp|zumapp|kakaostory|band|twitter|DaumApps|DaumDevice\/mobile|FB_IAB|FBAV|FB4A|FBAN|FBIOS|FBSS|TikTok/i;

  return mobileRegex.test(userAgent) || inAppBrowserRegex.test(userAgent);
};

export function resolveHostOSByUA(): UserAgentOS {
  if (typeof navigator === "undefined") {
    return "Unknown";
  }

  const ua = navigator.userAgent;

  if (/Windows NT/i.test(ua)) return "Windows";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";

  return "Unknown";
}

// NOTE: 한글을 LocalPart에서 포함하지 않기 위해 상위 문자열 SET을 local Part에 위치하지 못하게 설정
export function isEmailWithoutUTF8LocalPart(email: string): boolean {
  return isEmail(email, { allow_utf8_local_part: false });
}

export function isCanceledDocumentSign(status: DocumentSignStatus): boolean {
  return [
    DocumentSignStatus.Cancel,
    DocumentSignStatus.Canceled,
    DocumentSignStatus.Canceling,
    DocumentSignStatus.CancelingFailed,
  ].includes(status);
}

export function resolveDocumentSignMergeDownloadPollingPause(req: DocumentSignMergePollingType) {
  const { fileExportHistoryId, isPause } = req;
  return compareBigintSafe(fileExportHistoryId, BigInt(-1)) || isPause;
}

export function getFileUploadSizes(licenses: MySubscription | MySubscription[] | MySubscriptionLicense | MySubscriptionLicense[]): {
  uploadSizeLimitInMB: number;
  uploadSizeLimitInMBForHostOS: number;
} {
  const uploadSizeLimitInMB = resolveFileUploadMBSize(licenses);
  const uploadSizeLimitInMBForHostOS = resolveDisplayFileUploadMBSizeByOS(licenses);

  return { uploadSizeLimitInMB, uploadSizeLimitInMBForHostOS };
}

function resolveFileUploadMBSize(licenses: MySubscription | MySubscription[] | MySubscriptionLicense | MySubscriptionLicense[]): number {
  return isSubscribedFunctionality(licenses, BundleItemFunctionalityType.Docs_Viewer_Upload_Size_Limitless)
    ? FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMITLESS_IN_MIB // NOTE: 전자서명 요청 비동기 변경 이전까지 임시제한
    : FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMIT_IN_TEN_MIB;
}

function resolveDisplayFileUploadMBSizeByOS(licenses: MySubscription | MySubscription[] | MySubscriptionLicense | MySubscriptionLicense[]) {
  const hostOS = resolveHostOSByUA();

  const hasLimitlessUploadSizeFunctionality = isSubscribedFunctionality(
    licenses,
    BundleItemFunctionalityType.Docs_Viewer_Upload_Size_Limitless
  );

  if (!hasLimitlessUploadSizeFunctionality) {
    return FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMIT_IN_TEN_MIB;
  }

  return hostOS === "Windows" ? FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMITLESS_IN_MIB : 245; // 235 MIB === approx 246MB
}

export function resolveExtensionStrBy(folderType: FolderType) {
  return [FolderType.NORMAL, FolderType.ECONSENT].includes(folderType) ? "PDF" : "PDF, Excel, Word and PPT";
}
